import KpiCard from '@/components/dashboard/KpiCard';
import ChartCard from '@/components/dashboard/ChartCard';
import StageCompareWidget from '@/components/dashboard/StageCompareWidget';
import useDashboardV2 from '@/hooks/useDashboardV2';
import { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, ScatterChart, Scatter, ZAxis, Legend, ComposedChart, Line,
} from 'recharts';
import { DollarSign, Award, Clock, Activity, PhoneCall, MapPin, Mail, MessageCircle, Target, Users, XCircle, Sparkles, Repeat } from 'lucide-react';
import { filtersToParams } from '@/components/dashboard/DashboardFilters';
import api from '@/lib/api';

const PALETTE = ['#10B981', '#1B9CEA', '#A5D62C', '#F59E0B', '#A855F7', '#EC4899', '#64748B'];
const TAB_KEY = 'ventas';
const VERTICAL_LABELS = {
  real_estate: { cycle: 'Cierres', currency: 'MXN' },
  automotive: { cycle: 'Ventas', currency: 'MXN' },
  education: { cycle: 'Inscripciones', currency: 'MXN' },
  generic: { cycle: 'Cierres', currency: 'MXN' },
};

const EMPTY = 'Sin datos para el rango seleccionado';
const TOOLTIP_STYLE = { background: '#1F2833', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 11, color: '#fff' };

const formatMoney = v => `$${(v ?? 0).toLocaleString('es-MX')}`;
const formatShortMoney = v => `$${((v ?? 0) / 1000).toFixed(1)}k`;
// Fix 6.4-Ventas: formatea segundos a una unidad legible (mismo criterio que MarketingTab).
const formatDuration = (seconds) => {
  if (seconds == null) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}min`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`;
  return `${(seconds / 86400).toFixed(1)}d`;
};

export default function VentasTab({ filters, vertical = 'generic' }) {
  const labels = VERTICAL_LABELS[vertical] || VERTICAL_LABELS.generic;
  const [oppStageA, setOppStageA] = useState('');
  const [oppStageB, setOppStageB] = useState('');

  const deepQ = useDashboardV2({ endpoint: 'sales-deep', filters, vertical });
  const perfQ = useDashboardV2({ endpoint: 'vendor-performance', filters, vertical, extra: { limit: 20 } });
  // Fix 6.4-Ventas: tiempo entre etapas de opportunities.stage -- deliberadamente SIN filtros
  // del header (pedido explicito del cliente), solo el par de etapas a comparar.
  const oppStageTimeQ = useDashboardV2({
    endpoint: 'opportunity-stage-time',
    extra: (oppStageA && oppStageB) ? { stage_a: oppStageA, stage_b: oppStageB } : {},
  });
  const oppStagesCatalog = oppStageTimeQ.data?.stages_catalog || [];
  useEffect(() => {
    if (!oppStageA && !oppStageB && oppStagesCatalog.length >= 2) {
      setOppStageA(oppStagesCatalog[0].id);
      setOppStageB(oppStagesCatalog[1].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oppStagesCatalog.length]);
  const oppStageName = (id) => oppStagesCatalog.find(s => s.id === id)?.name || '—';

  const kpis = deepQ.data?.kpis || {};
  const current = kpis.current || {};
  const deltas = kpis.deltas || {};
  const funnel = deepQ.data?.funnel_extended || [];
  const forecast = deepQ.data?.forecast_vs_real || { forecast: 0, real: 0, planned: 0, pipeline_weighted: 0, stages_have_probability: false, has_plan: false };
  const pipelineFunnel = deepQ.data?.pipeline_funnel || [];
  const cemDist = deepQ.data?.cem_distribution || [];
  const attribution = deepQ.data?.attribution_by_channel || [];
  const scoring = deepQ.data?.scoring || { supported: false, data: [] };
  const activities = deepQ.data?.activities || {};
  const activitiesByOrigen = deepQ.data?.activities_by_origen || { manual_ui: 0, ai_agent: 0, nurturing_engine: 0 };
  const [origenFilter, setOrigenFilter] = useState('all');
  const lostByCat = deepQ.data?.lost_by_category || [];
  const assignmentDist = deepQ.data?.assignment_distribution || [];

  const vendors = perfQ.data?.vendors || [];
  const [freqItems, setFreqItems] = useState([]);
  const [freqLoading, setFreqLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setFreqLoading(true);
    const p = filtersToParams(filters || {});
    if (p.seller_id) p.assigned_to = p.seller_id;
    api.get('/leads/high-frequency', { params: p }).then(r => {
      if (!cancelled) setFreqItems(r.data?.items || []);
    }).catch(() => {
      if (!cancelled) setFreqItems([]);
    }).finally(() => {
      if (!cancelled) setFreqLoading(false);
    });
    return () => { cancelled = true; };
  }, [
    filters?.date_from, filters?.date_to, filters?.seller_id, filters?.group_id,
    filters?.campaign_id, filters?.lead_source_id,
  ]);

  // Para "Desempeño por vendedor (barras por etapa)" — usamos los datos del vendor-performance
  // y los apilamos.
  const vendorStagesData = vendors.slice(0, 8).map(v => ({
    name: v.name,
    nuevos: Math.max(0, (v.leads || 0) - (v.won || 0) - (v.lost || 0) - (v.opportunities || 0)),
    pipeline: v.opportunities || 0,
    ganados: v.won || 0,
    perdidos: v.lost || 0,
  }));

  const forecastChartData = [
    { name: 'Meta (plan)', value: forecast.planned || forecast.forecast, fill: '#A855F7' },
    { name: 'Pipeline ponderado', value: forecast.pipeline_weighted || 0, fill: '#F59E0B' },
    { name: 'Venta real', value: forecast.real, fill: '#10B981' },
  ];

  const totalOrigen = (activitiesByOrigen.manual_ui || 0) + (activitiesByOrigen.ai_agent || 0) + (activitiesByOrigen.nurturing_engine || 0);
  const origenShare = origenFilter === 'all' || !totalOrigen
    ? 1
    : (activitiesByOrigen[origenFilter] || 0) / totalOrigen;

  const activitiesData = [
    { name: 'Llamadas', value: Math.round((activities.llamadas || 0) * origenShare), icon: PhoneCall, color: '#1B9CEA' },
    { name: 'Visitas',  value: Math.round((activities.visitas  || 0) * origenShare), icon: MapPin,    color: '#A5D62C' },
    { name: 'Mensajes', value: Math.round((activities.mensajes || 0) * origenShare), icon: MessageCircle, color: '#A855F7' },
    { name: 'Correos',  value: Math.round((activities.correos  || 0) * origenShare), icon: Mail,      color: '#F59E0B' },
  ];
  const activitiesEmpty = activitiesData.every(a => a.value === 0);

  return (
    <div className="space-y-6" data-testid="tab-ventas-content">
      {/* KPIs con comparativa vs periodo anterior -- fila unica, scroll horizontal
          en movil (feedback owner jul2026) */}
      <div className="flex overflow-x-auto gap-3 pb-1">
        <KpiCard icon={DollarSign} label={`Valor ${labels.cycle.toLowerCase()}`} value={deepQ.loading ? '—' : formatShortMoney(current.won_value)} sub={labels.currency} delta={deltas.won_value != null ? { value: deltas.won_value } : null} color="emerald" loading={deepQ.loading} testid="ventas-kpi-won-value" />
        <KpiCard icon={Activity} label="Valor pipeline activo" value={deepQ.loading ? '—' : formatShortMoney(current.pipeline_value)} sub={labels.currency} delta={deltas.pipeline_value != null ? { value: deltas.pipeline_value } : null} color="amber" loading={deepQ.loading} testid="ventas-kpi-pipeline" />
        <KpiCard icon={Target} label="Tasa de cierre" value={deepQ.loading ? '—' : `${current.close_rate ?? 0}%`} sub={`${current.won_count ?? 0} ganados`} delta={deltas.close_rate != null ? { value: deltas.close_rate } : null} color="cyan" loading={deepQ.loading} testid="ventas-kpi-close-rate" />
        <KpiCard icon={Clock} label="Tiempo promedio cierre" value={deepQ.loading ? '—' : `${current.avg_close_days ?? 0}d`} sub="días" delta={deltas.avg_close_days != null ? { value: -deltas.avg_close_days } : null} color="violet" loading={deepQ.loading} testid="ventas-kpi-time-close" />
        <KpiCard
          icon={Repeat}
          label="Tiempo promedio entre etapas"
          value={oppStageTimeQ.loading ? '—' : formatDuration(oppStageTimeQ.data?.avg_seconds_between_any_stage_change)}
          sub={`${oppStageTimeQ.data?.n_transitions ?? 0} transiciones registradas`}
          color="violet"
          loading={oppStageTimeQ.loading}
          testid="ventas-kpi-stage-time"
        />
        {/* Fix 6.4-Ventas: comparador de tiempo entre dos etapas -- en la MISMA
            fila que los KPIs, no en un renglon aparte (feedback owner jul2026) */}
        {oppStagesCatalog.length > 0 && (
          <StageCompareWidget
            stages={oppStagesCatalog}
            stageA={oppStageA}
            stageB={oppStageB}
            onChangeA={setOppStageA}
            onChangeB={setOppStageB}
            value={formatDuration(oppStageTimeQ.data?.comparison?.avg_seconds)}
            n={oppStageTimeQ.data?.comparison?.n}
            loading={oppStageTimeQ.loading}
            testid="ventas-stage-compare"
          />
        )}
      </div>

      {/* CEM vs CRM: calificaciones de leads · pipeline de oportunidades · forecast planeado */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Calificaciones CEM (interés del lead)" icon={Activity} loading={deepQ.loading} empty={!cemDist.length && !funnel.length} emptyMessage={EMPTY} testid="ventas-chart-funnel" downloadable tabKey={TAB_KEY} chartId="ventas-chart-funnel">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={cemDist.length ? cemDist : funnel} layout="vertical" margin={{ left: 30, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis type="number" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} label={{ value: 'Leads', position: 'insideBottom', offset: -5, style: { fill: '#8893A7', fontSize: 10 } }} />
              <YAxis dataKey="stage" type="category" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} width={120} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                {(cemDist.length ? cemDist : funnel).map((d, i) => <Cell key={i} fill={d.color || PALETTE[i % PALETTE.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Pipeline de oportunidades (CRM)" icon={Target} loading={deepQ.loading} empty={!pipelineFunnel.length} emptyMessage="Sin oportunidades en el rango. El Kanban de Oportunidades alimenta este gráfico." testid="ventas-chart-pipeline" downloadable tabKey={TAB_KEY} chartId="ventas-chart-pipeline">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={pipelineFunnel} layout="vertical" margin={{ left: 30, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis type="number" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} label={{ value: 'Oportunidades', position: 'insideBottom', offset: -5, style: { fill: '#8893A7', fontSize: 10 } }} />
              <YAxis dataKey="stage" type="category" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} width={110} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v, n) => n === 'value' ? [formatMoney(v), 'Valor'] : [v, 'Oportunidades']} />
              <Bar dataKey="count" name="Oportunidades" radius={[0, 4, 4, 0]} fill="#1B9CEA" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Forecast vs Venta real" icon={Sparkles} loading={deepQ.loading} empty={forecastChartData.every(d => !d.value)} emptyMessage={forecast.has_plan ? EMPTY : 'Define la meta mensual en Configuración → Calificaciones Forecast'} testid="ventas-chart-forecast" downloadable tabKey={TAB_KEY} chartId="ventas-chart-forecast">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={forecastChartData} margin={{ left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="name" tick={{ fill: '#8893A7', fontSize: 11 }} axisLine={false} />
              <YAxis tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} tickFormatter={formatShortMoney} label={{ value: 'Valor ($)', angle: -90, position: 'insideLeft', offset: 10, style: { fill: '#8893A7', fontSize: 10 } }} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={v => [formatMoney(v), 'Valor']} />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {forecastChartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Scoring IA vs seller + Actividades comerciales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Calificación de leads · IA Score vs Seller Score" icon={Sparkles} loading={deepQ.loading} empty={!scoring.supported || !scoring.data.length} emptyMessage="Califica leads en el panel (seller score 1-10). El Score IA se calcula solo." testid="ventas-chart-scoring" downloadable tabKey={TAB_KEY} chartId="ventas-chart-scoring">
          <ResponsiveContainer width="100%" height={280}>
            <ScatterChart margin={{ left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis type="number" dataKey="manual_score" name="Seller" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} domain={[0, 10]} label={{ value: 'Seller Score', position: 'insideBottom', offset: -5, style: { fill: '#8893A7', fontSize: 10 } }} />
              <YAxis type="number" dataKey="ai_score" name="IA" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} domain={[0, 10]} label={{ value: 'IA Score', angle: -90, position: 'insideLeft', offset: 10, style: { fill: '#8893A7', fontSize: 10 } }} />
              <ZAxis range={[60, 60]} />
              <Tooltip cursor={{ strokeDasharray: '3 3' }} contentStyle={TOOLTIP_STYLE} />
              <Scatter data={scoring.data} fill="#A855F7" />
            </ScatterChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Actividades comerciales" icon={Activity} loading={deepQ.loading} empty={activitiesEmpty} emptyMessage="Sin actividades registradas en activity_logs" testid="ventas-chart-activities" tabKey={TAB_KEY} chartId="ventas-chart-activities">
          <div className="px-3 pt-3 flex items-center justify-end gap-2" data-testid="ventas-activities-origen-filter">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Origen</span>
            <Select value={origenFilter} onValueChange={setOrigenFilter}>
              <SelectTrigger className="h-7 w-44 text-xs bg-black/20 border-white/10" data-testid="ventas-activities-origen-select">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="manual_ui">Manual UI (humano)</SelectItem>
                <SelectItem value="ai_agent">AI Agent</SelectItem>
                <SelectItem value="nurturing_engine">Nurturing Engine</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3 p-3">
            {activitiesData.map(a => {
              const Icon = a.icon;
              return (
                <div key={a.name} className="flex items-center gap-3 p-3 rounded-lg bg-black/20 border border-white/5" data-testid={`activity-${a.name.toLowerCase()}`}>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: a.color + '20' }}>
                    <Icon className="w-4 h-4" style={{ color: a.color }} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{a.name}</p>
                    <p className="text-xl font-bold font-heading" style={{ color: a.color }}>{a.value}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="px-3 pb-3 flex items-center justify-between text-[11px] text-muted-foreground border-t border-white/5 pt-2" data-testid="ventas-activities-origen-breakdown">
            <span className={origenFilter !== 'all' && origenFilter !== 'manual_ui' ? 'opacity-40' : ''}>Humano: <span className="text-emerald-400 font-semibold">{activitiesByOrigen.manual_ui || 0}</span></span>
            <span className={origenFilter !== 'all' && origenFilter !== 'ai_agent' ? 'opacity-40' : ''}>AI Agent: <span className="text-purple-300 font-semibold">{activitiesByOrigen.ai_agent || 0}</span></span>
            <span className={origenFilter !== 'all' && origenFilter !== 'nurturing_engine' ? 'opacity-40' : ''}>Nurturing: <span className="text-blue-300 font-semibold">{activitiesByOrigen.nurturing_engine || 0}</span></span>
          </div>
        </ChartCard>
      </div>

      {/* Atribución por canal + Distribución de asignación */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Atribución por canal (ganados)" icon={Award} loading={deepQ.loading} empty={!attribution.length} emptyMessage={EMPTY} testid="ventas-chart-attribution" downloadable tabKey={TAB_KEY} chartId="ventas-chart-attribution">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={attribution} margin={{ left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="channel" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} />
              <YAxis tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} label={{ value: 'Ganados', angle: -90, position: 'insideLeft', offset: 10, style: { fill: '#8893A7', fontSize: 10 } }} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="won_leads" radius={[4, 4, 0, 0]}>
                {attribution.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Distribución de asignación de leads" icon={Users} loading={deepQ.loading} empty={!assignmentDist.length} emptyMessage={EMPTY} testid="ventas-chart-assignment" downloadable tabKey={TAB_KEY} chartId="ventas-chart-assignment">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={assignmentDist.slice(0, 10)} layout="vertical" margin={{ left: 70, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis type="number" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} label={{ value: 'Leads', position: 'insideBottom', offset: -5, style: { fill: '#8893A7', fontSize: 10 } }} />
              <YAxis dataKey="vendor" type="category" tick={{ fill: '#8893A7', fontSize: 9 }} axisLine={false} width={100} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="leads" radius={[0, 4, 4, 0]} fill="#1B9CEA" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Desempeño por vendedor (barras por etapa) */}
      <ChartCard title="Desempeño por vendedor (leads por etapa)" icon={Award} loading={perfQ.loading} empty={!vendorStagesData.length} emptyMessage={EMPTY} testid="ventas-chart-vendor-stages" height={320} downloadable tabKey={TAB_KEY} chartId="ventas-chart-vendor-stages">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={vendorStagesData} margin={{ left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="name" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} />
            <YAxis tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} label={{ value: 'Leads', angle: -90, position: 'insideLeft', offset: 10, style: { fill: '#8893A7', fontSize: 10 } }} />
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Bar dataKey="nuevos" stackId="a" name="Nuevos" fill="#6366F1" />
            <Bar dataKey="pipeline" stackId="a" name="Pipeline" fill="#F59E0B" />
            <Bar dataKey="ganados" stackId="a" name="Ganados" fill="#10B981" />
            <Bar dataKey="perdidos" stackId="a" name="Perdidos" fill="#EF4444" />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Tabla: Desempeño por vendedor */}
      <ChartCard title="Desempeño por vendedor · detalle" icon={Users} loading={perfQ.loading} empty={!vendors.length} emptyMessage={EMPTY} testid="ventas-table-vendors" tabKey={TAB_KEY} chartId="ventas-table-vendors">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-white/5">
                <th className="py-2 px-3 font-normal">Vendedor</th>
                <th className="py-2 px-3 font-normal text-right">Ventas</th>
                <th className="py-2 px-3 font-normal text-right">Pipeline ({labels.currency})</th>
                <th className="py-2 px-3 font-normal text-right">Tasa cierre</th>
                <th className="py-2 px-3 font-normal text-right">Perdidas</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map((v, i) => (
                <tr key={v.vendor_id} className={`border-b border-white/[0.02] ${i === 0 ? 'bg-emerald-500/5' : ''}`} data-testid={`ventas-tbl-vendor-${v.vendor_id}`}>
                  <td className="py-2 px-3 text-white">{v.name}</td>
                  <td className="py-2 px-3 text-right text-emerald-400 font-semibold">{v.won}</td>
                  <td className="py-2 px-3 text-right text-amber-300">{formatMoney(v.pipeline_value)}</td>
                  <td className="py-2 px-3 text-right text-cyan-300">{v.conversion_rate}%</td>
                  <td className="py-2 px-3 text-right text-rose-400">{v.lost}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      <ChartCard
        title="Leads de alta frecuencia"
        icon={Repeat}
        loading={freqLoading}
        empty={!freqItems.length}
        emptyMessage="Nadie pidió información más de una vez en este filtro"
        testid="ventas-high-frequency"
        tabKey={TAB_KEY}
        chartId="ventas-high-frequency"
      >
        <p className="text-[10px] text-muted-foreground px-1 pb-2">
          Mismo contacto, varias consultas. El ID es el lead original. Obedece los filtros de arriba.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-white/5">
                <th className="py-2 px-3 font-normal">Lead</th>
                <th className="py-2 px-3 font-normal">Medio / campaña</th>
                <th className="py-2 px-3 font-normal text-right">Frecuencia</th>
                <th className="py-2 px-3 font-normal">ID original</th>
              </tr>
            </thead>
            <tbody>
              {freqItems.map((row) => (
                <tr key={row.original_lead_id} className="border-b border-white/[0.02]" data-testid={`ventas-freq-${row.original_lead_id}`}>
                  <td className="py-2 px-3 text-white">{row.name}</td>
                  <td className="py-2 px-3 text-muted-foreground">{row.channel || row.source || '—'}</td>
                  <td className="py-2 px-3 text-right font-semibold text-amber-300">{row.frequency}</td>
                  <td className="py-2 px-3 font-mono text-[10px] text-muted-foreground">{String(row.original_lead_id || '').slice(0, 8)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* Perdidos por categoría y sub-motivo */}
      <ChartCard title="Leads perdidos por categoría y sub-motivo" icon={XCircle} loading={deepQ.loading} empty={!lostByCat.length} emptyMessage="Sin leads perdidos con razón registrada (lead.lost_category, lead.lost_reason)" testid="ventas-chart-lost" tabKey={TAB_KEY} chartId="ventas-chart-lost">
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={lostByCat.slice(0, 12)} layout="vertical" margin={{ left: 120, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis type="number" tick={{ fill: '#8893A7', fontSize: 10 }} axisLine={false} label={{ value: 'Perdidos', position: 'insideBottom', offset: -5, style: { fill: '#8893A7', fontSize: 10 } }} />
            <YAxis dataKey="reason" type="category" tick={{ fill: '#8893A7', fontSize: 9 }} axisLine={false} width={150} />
            <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v, _n, p) => [`${v} (${p.payload.category})`, 'Perdidos']} />
            <Bar dataKey="count" radius={[0, 4, 4, 0]} fill="#EF4444" />
            <Line type="monotone" dataKey="count" stroke="#F59E0B" strokeWidth={0} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}
